import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { useI18n } from "../contexts/I18nContext";
import { useCart } from "../contexts/CartContext";
import { fileUrl } from "../lib/api";
import { Minus, Plus, X } from "lucide-react";

export default function CartPage() {
  const { t } = useI18n();
  const { items, total, removeItem, updateQty } = useCart();
  const navigate = useNavigate();

  return (
    <MarketplaceLayout>
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="cart-page">
        <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tight mb-12">{t.cart.title}</h1>

        {items.length === 0 ? (
          <div className="py-24 text-center">
            <p className="overline text-neutral-400" data-testid="empty-cart">{t.cart.empty}</p>
            <Link to="/" className="mt-8 inline-block bg-black text-white px-8 py-4 overline hover:bg-neutral-800 transition-colors">
              {t.cart.continue}
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 divide-y divide-neutral-200 border-t border-b border-neutral-200">
              {items.map((it) => (
                <div key={it.key} className="py-6 grid grid-cols-12 gap-6 items-center" data-testid={`cart-item-${it.key}`}>
                  <img src={fileUrl(it.image_path)} alt={it.title} className="col-span-3 sm:col-span-2 aspect-square object-cover bg-neutral-50" />
                  <div className="col-span-6 sm:col-span-5">
                    <div className="font-serif text-xl">{it.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{it.artist_name}</div>
                    {it.size_label && (
                      <div className="text-xs text-neutral-700 mt-1">{it.size_label} · {it.width_cm}×{it.height_cm} cm</div>
                    )}
                    {it.material_name && (
                      <div className="text-xs text-neutral-500 mt-0.5">{it.material_name}</div>
                    )}
                    {it.framing_label && (
                      <div className="text-xs text-neutral-900 mt-0.5" data-testid={`cart-framing-${it.key}`}>{it.framing_label}</div>
                    )}
                  </div>
                  <div className="col-span-3 flex items-center border border-neutral-200">
                    <button onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2 hover:bg-neutral-100" data-testid={`qty-minus-${it.key}`}><Minus size={14} /></button>
                    <div className="flex-1 text-center text-sm">{it.quantity}</div>
                    <button onClick={() => updateQty(it.key, it.quantity + 1)} className="p-2 hover:bg-neutral-100" data-testid={`qty-plus-${it.key}`}><Plus size={14} /></button>
                  </div>
                  <div className="col-span-9 sm:col-span-2 text-right font-serif text-lg">€{(it.price * it.quantity).toFixed(2)}</div>
                  <button onClick={() => removeItem(it.key)} className="col-span-3 sm:col-span-1 text-neutral-400 hover:text-black flex justify-end" data-testid={`remove-${it.key}`}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            <div className="lg:col-span-4">
              <div className="border border-neutral-200 p-8 sticky top-24">
                <div className="flex justify-between overline mb-6">
                  <span>{t.cart.subtotal}</span>
                  <span className="font-serif text-2xl normal-case tracking-normal" data-testid="cart-total">€{total.toFixed(2)}</span>
                </div>
                <button onClick={() => navigate("/checkout")} className="w-full bg-black text-white py-4 overline hover:bg-neutral-800 transition-colors" data-testid="checkout-btn">
                  {t.cart.checkout}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </MarketplaceLayout>
  );
}
